import React, { useMemo } from "react";
import Header from "../component/Header";
import CommonBanner from "../component/CommonBanner";
import Footer from "../component/Footer";
import ExploreResultLocations from "../component/ExploreResultLocations";
import { Card, Col, Container, Row, Button, Form } from "react-bootstrap";
import PhoneNumber from "../assets/images/phone-number.svg";
import EmailUs from "../assets/images/email-us.svg";
import ConnectUs from "../assets/images/connect-us.svg";
import Facebook from "../assets/images/facebook-2.svg";
import X from "../assets/images/x-2.svg";
import Instagram from "../assets/images/instagram-2.svg";
import YouTube from "../assets/images/youtube-2.svg";
import LinkedIn from "../assets/images/linkedin.svg";
import { usePageContent } from "../hooks/usePageContent";
import { CONTACT_US } from "../api/constants";
import Swal from "sweetalert2";
import { Helmet } from "react-helmet-async";

function ContactUs() {
  const { content, loading, error } = usePageContent("contact-us");
  const [first_name, setFirstName] = React.useState("");
  const [last_name, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [showForm, setShowForm] = React.useState(false);

  const contactSection = useMemo(() => {
    if (!content?.contact_us_section) return null;

    if (typeof content.contact_us_section === "string") {
      try {
        return JSON.parse(content.contact_us_section);
      } catch (err) {
        console.error("Failed to parse contact_us_section JSON:", err);
        return null;
      }
    }

    return content.contact_us_section;
  }, [content]);

  const normalizeHtmlBreaks = (s) =>
    s
      ?.replace(/<span[^>]*>|<\/span>/gi, "")
      .replace(/<br\s*\/?\>/gi, "\n")
      .replace(/\r\n/g, "\n")
      .replace(/\\r\\n/g, "\n");

  const descriptionLines = contactSection?.description
    ? normalizeHtmlBreaks(contactSection.description)
        .split(/\r?\n/)
        .filter(Boolean)
    : [
        "We’re here to help!",
        "Our National Customer Service Team is available 8am - 5pm PST, seven days a week.",
      ];

  const socialLinks = [
    { url: contactSection?.facebook_url, icon: Facebook, alt: "Facebook" },
    { url: contactSection?.twitter_url, icon: X, alt: "X" },
    { url: contactSection?.instagram_url, icon: Instagram, alt: "Instagram" },
    { url: contactSection?.youtube_url, icon: YouTube, alt: "YouTube" },
    { url: contactSection?.linkedin_url, icon: LinkedIn, alt: "LinkedIn" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(""); // Clear previous error message
    //validate
    if (!first_name || !last_name || !email || !message) {
      setErrorMessage("Please fill in all required fields.");
      return;
    }
    const formData = {
      first_name: first_name,
      last_name: last_name,
      email: email,
      phone: phone,
      message: message,
    };
    //submit
    try {
      const response = await fetch(CONTACT_US, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      // Check if the response is successful
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      } else {
        // Handle successful submission
        if (data.status) {
          Swal.fire({
            title: "Success!",
            text: "Your message has been sent successfully.",
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "#0abab5",
          });
        }
        // Clear form fields
        setFirstName("");
        setLastName("");
        setEmail("");
        setPhone("");
        setMessage("");
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setErrorMessage("Failed to submit the form. Please try again.");
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${content?.page_name || "Contact Us"} | Jambobay`}</title>
        <meta
          name="description"
          content="Contact the Jambobay team for support, questions, or partnership inquiries."
        />
        <meta
          name="keywords"
          content="Contact Us, Jambobay, Support, Real Estate Help, Contact"
        />
      </Helmet>
      <div className="main contact-us-page">
        <Header />
        <CommonBanner title="Contact Us" />
        <div className="contact-us common-padding">
          <Container>
            <div className="common-header text-center">
              <h2>{contactSection?.title || "Get In Touch"}</h2>
              <p>
                {descriptionLines.map((line, index) => (
                  <React.Fragment key={index}>
                    {line.trim()}
                    {index < descriptionLines.length - 1 ? <br /> : null}
                  </React.Fragment>
                ))}
              </p>
            </div>
            <Row className="g-4 justify-content-center">
              <Col md={4}>
                <Card>
                  <img src={PhoneNumber} alt="" />
                  <p>{contactSection?.call_us_title || "Phone Number"}</p>
                  <h5>
                    <a
                      href={`tel:${contactSection?.call_us_no || "+19876543210"}`}
                      title=""
                    >
                      {contactSection?.call_us_no || "+1 9876543210"}
                    </a>
                  </h5>
                </Card>
              </Col>
              <Col md={4}>
                <Card>
                  <img src={EmailUs} alt="" />
                  <p>{contactSection?.email_us_title || "Email Us"}</p>
                  <Button
                    type="button"
                    className="custom-bttn style-2 mt-3"
                    onClick={() => setShowForm((prev) => !prev)}
                  >
                    {showForm ? "Hide Form" : "Email Now"}
                  </Button>
                </Card>
              </Col>
              <Col md={4}>
                <Card>
                  <img src={ConnectUs} alt="" />
                  <p>{contactSection?.connect_title || "Connect with us"}</p>
                  <ul>
                    {socialLinks.map((social) =>
                      social.url ? (
                        <li key={social.alt}>
                          <a href={social.url} target="_blank" rel="noreferrer">
                            <img src={social.icon} alt={social.alt} />
                          </a>
                        </li>
                      ) : null,
                    )}
                  </ul>
                </Card>
              </Col>
            </Row>
            {showForm && (
              <Form className="mt-4" onSubmit={handleSubmit}>
                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Label>
                      First Name <span>*</span>
                    </Form.Label>
                    <Form.Control
                      name="first_name"
                      type="text"
                      placeholder="Ex. John"
                      value={first_name}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>
                      Last Name <span>*</span>
                    </Form.Label>
                    <Form.Control
                      name="last_name"
                      type="text"
                      placeholder="Ex. Doe"
                      value={last_name}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>
                      Email <span>*</span>
                    </Form.Label>
                    <Form.Control
                      name="email"
                      type="email"
                      placeholder="Ex. john.doe@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>
                      Phone <span>*</span>
                    </Form.Label>
                    <Form.Control
                      name="phone"
                      type="number"
                      maxLength={12}
                      placeholder="Ex. 07181818182"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label>
                    What can we help you with? <span>*</span>
                  </Form.Label>
                  <Form.Control
                    name="message"
                    as="textarea"
                    rows={3}
                    placeholder="Write here..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                </Form.Group>

                <Button type="submit" className="custom-bttn style-1">
                  Submit
                </Button>
                {errorMessage && (
                  <div
                    className="error-message"
                    style={{ color: "red", marginTop: "10px" }}
                  >
                    {errorMessage}
                  </div>
                )}
              </Form>
            )}
          </Container>
        </div>
        <ExploreResultLocations />
        <Footer />
      </div>
    </>
  );
}
export default ContactUs;
